import { useMemo } from 'react'
import { SparkleLoader } from '../../../../common/SparkleLoader'
import { StakeholderAvatar } from '../../../../../features/stakeholder'
import {
  StakeholderCTAItem,
  StakeholderCTAInfo,
  StakeholderCTADetails,
  StakeholderCTAName,
  StakeholderCTARole,
  AskFeedbackButton,
} from '../styles/StakeholderCTAStyles'
import type { StakeholderRatings, ProjectStakeholder } from '@bandos/shared'
import type { CategoryStyle } from '../types'

interface CharacterModal {
  openModal: (templateId?: string) => void
}

interface StakeholderCTAListProps {
  ratings: StakeholderRatings
  categoryStyle: CategoryStyle
  generatingNames: string[]
  projectStakeholders: ProjectStakeholder[]
  onGenerateRatings?: (names?: string[]) => void
  characterModal: CharacterModal | null
}

/**
 * Renders "Ask for feedback" CTAs for stakeholders who haven't rated yet,
 * plus an "Add team" CTA when no stakeholders exist.
 */
export function StakeholderCTAList({
  ratings,
  categoryStyle,
  generatingNames,
  projectStakeholders,
  onGenerateRatings,
  characterModal,
}: StakeholderCTAListProps): React.JSX.Element {
  const unratedStakeholders = useMemo(
    () =>
      projectStakeholders.filter((ps) => {
        const name = ps.template?.name
        if (!name) return false
        return !ratings.stakeholders.some((s) => s.name === name)
      }),
    [projectStakeholders, ratings.stakeholders]
  )

  return (
    <>
      {unratedStakeholders.map((ps) => {
        const name = ps.template?.name ?? 'Team Member'
        const isLoading = generatingNames.includes(name)
        return (
          <StakeholderCTAItem
            key={ps.id}
            className="nodrag"
            $accentColor={categoryStyle.accentColor}
            $isLoading={isLoading}
          >
            <StakeholderCTAInfo>
              <StakeholderAvatar
                name={name}
                portraitUrl={ps.template?.portrait_url ?? null}
                size="sm"
              />
              <StakeholderCTADetails>
                <StakeholderCTAName>{name}</StakeholderCTAName>
                <StakeholderCTARole>{ps.template?.role ?? 'Stakeholder'}</StakeholderCTARole>
              </StakeholderCTADetails>
            </StakeholderCTAInfo>
            <AskFeedbackButton
              $accentColor={categoryStyle.accentColor}
              $isLoading={isLoading}
              disabled={isLoading}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                onGenerateRatings?.([name])
              }}
            >
              {isLoading ? (
                <>
                  <SparkleLoader isAnimating={true} size={30} />
                  <span>Asking...</span>
                </>
              ) : (
                <>
                  <SparkleLoader isAnimating={false} size={30} />
                  <span>Ask for feedback</span>
                </>
              )}
            </AskFeedbackButton>
          </StakeholderCTAItem>
        )
      })}

      {projectStakeholders.length === 0 && characterModal && (
        <StakeholderCTAItem
          className="nodrag"
          $accentColor={categoryStyle.accentColor}
          $isLoading={false}
        >
          <StakeholderCTAInfo>
            <StakeholderAvatar name="Team" portraitUrl={null} size="sm" />
            <StakeholderCTADetails>
              <StakeholderCTAName>Want more perspectives?</StakeholderCTAName>
              <StakeholderCTARole>See how your team would rate this</StakeholderCTARole>
            </StakeholderCTADetails>
          </StakeholderCTAInfo>
          <AskFeedbackButton
            $accentColor={categoryStyle.accentColor}
            $isLoading={false}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              characterModal.openModal()
            }}
          >
            <span>Add team</span>
          </AskFeedbackButton>
        </StakeholderCTAItem>
      )}
    </>
  )
}
